<template>
  <div class="gs-player-section">
    <div class="gs-switch-row">
      <span class="gs-switch-row__text">
        <span class="gs-switch-row__label">{{ $t('frontend_groovevideo_videos_step2_show_play_button') }}</span>
        <span class="gs-switch-row__help">
          The icon in the centre of the video before a viewer starts it.
        </span>
      </span>
      <gd-toggle v-model="model.play_button.enable" @change="emitChange()" />
    </div>

    <template v-if="model.play_button.enable">
      <div class="gs-switch-row">
        <span class="gs-switch-row__text">
          <span class="gs-switch-row__label">Show it again on pause</span>
          <span class="gs-switch-row__help">
            Brings the icon back whenever the viewer pauses.
          </span>
        </span>
        <gd-toggle v-model="model.play_button.show_on_pause" @change="emitChange()" />
      </div>

      <modern-prompts v-if="isModern" :model="model" kind="play" @change="emitChange()" />
      <template v-else>
      <gs-field label="Play button">
        <gd-dropdown
          v-model="model.play_button.type"
          :options="playIconOptions"
          size="full"
          class="text-left"
          @change="changePlayButtonType"
        ></gd-dropdown>
      </gs-field>

      <gs-field v-if="model.play_button.type == 2" label="Icon">
        <image-library-picker
          :value="model.play_button.library_source"
          :count="29"
          :asset-path="playButtons"
          pattern="playbutton{n}.png"
          label="Select an image"
          @input="setPlayButtonLibrary($event)"
        />
      </gs-field>

      <gs-field
        v-else-if="model.play_button.type == 3"
        label="Your icon"
        :error="customSourceError"
      >
        <div class="imagePicker">
          <image-picker
            :primary="true"
            v-model="model.play_button.custom_source"
            @change="emitChange()"
          ></image-picker>
        </div>
      </gs-field>

      <legacy-overlay-preview :model="model" kind="play" />
      <div v-if="model.play_button.type" class="gs-player-section__pair">
        <gs-field label="Animation">
          <dropdown-with-scroll
            v-model="model.play_button.animation"
            :options="animationDataOptions"
            class="text-left"
            max-height="150px"
            @change="emitChange()"
          ></dropdown-with-scroll>
        </gs-field>

        <gs-field v-if="[2, 3].includes(Number(model.play_button.type))" label="Icon size">
          <gd-dropdown
            v-model="model.play_button.size"
            :options="unmuteImageSizeOptions"
            size="full"
            class="text-left"
            @change="emitChange()"
          ></gd-dropdown>
        </gs-field>
      </div>
      </template>
    </template>
  </div>
</template>

<script>
import LegacyOverlayPreview from './LegacyOverlayPreview.vue';
import ModernPrompts from './ModernPrompts.vue';
import { isModernVideo } from './newAppearance';
import DropdownWithScroll from '@/components/DropdownWithScroll';
import ImagePicker from '@groovepages/gd-ui-kit/components/ImagePickerModal';
import { requiredIf } from 'vuelidate/lib/validators';
import ImageLibraryPicker from './ImageLibraryPicker.vue';
import { animationOptions, sizeOptions } from './playerOptions';

// webpack needs a static path here; the shared picker only calls into it.
const PLAY_BUTTONS = require.context(
  '../../../../assets/images/icons/play-buttons',
  false,
  /\.png$/
);

export default {
  name: 'PlayButtonSection',

  components: { LegacyOverlayPreview, ModernPrompts, DropdownWithScroll, ImagePicker, ImageLibraryPicker },

  props: {
    model: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      playButtons: PLAY_BUTTONS,
    };
  },

  // Ported from step2, unchanged.
  validations: {
    model: {
      play_button: {
        custom_source: {
          required: requiredIf(function(object) {
            return this.model.play_button.enable && Number(object.type) === 3;
          }),
        },
      },
    },
  },

  computed: {
    isModern() { return isModernVideo(this.model); },
    animationDataOptions() {
      return animationOptions(this.$t.bind(this));
    },

    unmuteImageSizeOptions() {
      return sizeOptions(this.$t.bind(this));
    },

    playIconOptions() {
      return [
        { text: this.$t('frontend_groovevideo_videos_step2_default_play_button'), value: 1 },
        { text: this.$t('frontend_groovevideo_videos_step2_select_library'), value: 2 },
        { text: this.$t('frontend_groovevideo_videos_step2_upload_own'), value: 3 },
      ];
    },

    customSourceError() {
      return this.$v.model.play_button.custom_source.$error
        ? 'Choose the icon viewers press to start the video.'
        : '';
    },
  },

  methods: {
    emitChange() {
      this.$emit('updateModel', this.model);
      this.$emit('forceRerender');
    },

    // Ported from step2.
    changePlayButtonType() {
      const playButton = this.model.play_button;
      if (playButton.type == 2 && playButton.library_source === '') {
        this.model.play_button.library_source = 'playbutton14.png';
      }
      this.emitChange();
    },

    // Ported from step2.
    setPlayButtonLibrary(value) {
      this.model.play_button.library_source = value;
      this.emitChange();
    },

    validate() {
      this.$v.$touch();
      const isValid = !this.$v.$invalid;
      this.$emit('on-validate', isValid);
      return isValid;
    },
  },
};
</script>

<style scoped>
.gs-player-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 14px;
}

.gs-player-section__pair {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.gs-switch-row {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  justify-content: space-between;
  padding: 14px 16px;
  background: var(--gs-surface-subtle);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
}

.gs-switch-row__text {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.gs-switch-row__label {
  color: var(--gs-foreground);
  font-size: 14px;
  font-weight: 600;
  line-height: 1.35;
}

.gs-switch-row__help {
  max-width: 56ch;
  color: var(--gs-muted-foreground);
  font-size: 13px;
  line-height: 1.5;
}

@media (max-width: 640px) {
  .gs-player-section__pair {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
